import React, { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import catFurDiaryImage from '../../assets/cat-fur-diary.png';
import ballImage from '../../assets/ball.png';
import milkSteamImage from '../../assets/milksteam.png';
import { resolveLineRevealProgress } from './LetterRevealSection';
import './WritingSection.css';

type WritingLanguage = 'en' | 'zh';

type WritingArticle = {
  index: string;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  href?: string;
};

type WritingCopy = {
  title: string;
  intro: string;
  articles: WritingArticle[];
};

type WritingSectionProps = {
  language?: WritingLanguage;
};

const WRITING_COPY: Record<WritingLanguage, WritingCopy> = {
  en: {
    title: 'Notes I am saving for later.',
    intro:
      'Essays, learning notes, and small observations from work, study, and daily life.',
    articles: [
      {
        index: '2026 July',
        title: 'If AI Is So Powerful, Why Should We Still Try?',
        summary:
          'A note about AI, effort, training, and why the process can still change the person who goes through it.',
        image: ballImage,
        imageAlt: 'Glass spheres on a glowing green surface for an AI essay cover',
        href: '/writing/ai-why-still-try',
      },
      {
        index: '2026 Jun',
        title: "Isn't This How You Steam Milk?",
        summary:
          'A shift with an automatic espresso machine, and a small realization about tools, control, and the shape of common sense.',
        image: milkSteamImage,
        imageAlt: 'Steam wand texturing milk in a metal jug for a cafe observation article cover',
        href: '/writing/milk-foam-not-like-this',
      },
      {
        index: '2026 May',
        title: 'I Pulled a Little Tuft of Cat Fur From My Pocket',
        summary:
          'A quiet reminder, found during a crowded cafe shift, that a life is larger than the motions assigned to it.',
        image: catFurDiaryImage,
        imageAlt: 'A small grey-white ball of cat fur resting in soft light',
        href: '/writing/cat-fur-in-my-pocket',
      },
    ],
  },
  zh: {
    title: '我想慢慢留下来的笔记。',
    intro:
      '把学习、工作和日常观察里反复出现的念头，慢慢整理成文章。',
    articles: [
      {
        index: '2026 July',
        title: 'AI 这么强，人还努力干嘛？',
        summary:
          '关于 AI、努力、训练，以及为什么过程仍然会改变那个经历过的人。',
        image: ballImage,
        imageAlt: '为 AI 随笔封面使用的绿色玻璃球图片',
        href: '/writing/ai-why-still-try',
      },
      {
        index: '2026 Jun',
        title: '奶泡不是这样打的吗？',
        summary:
          '一次顶班里遇到的全自动咖啡机，也是一点关于工具、控制和常识形状的观察。',
        image: milkSteamImage,
        imageAlt: '咖啡机蒸汽棒正在金属奶缸里打奶泡的文章封面',
        href: '/writing/milk-foam-not-like-this',
      },
      {
        index: '2026 May',
        title: '我从口袋里摸出小猫的毛',
        summary:
          '忙乱的咖啡店里，一小团猫毛轻轻提醒我：生活不只属于那些被安排好的动作。',
        image: catFurDiaryImage,
        imageAlt: '一小团灰白色猫毛安静地放在柔和光线里',
        href: '/writing/cat-fur-in-my-pocket',
      },
    ],
  },
};

const CJK_CHARACTER_PATTERN = /[\u3400-\u9fff\uf900-\ufaff]/;

const renderRevealText = (text: string, keyPrefix: string) => {
  if (CJK_CHARACTER_PATTERN.test(text)) {
    return Array.from(text).map((character, characterIndex) =>
      /^\s$/.test(character) ? (
        <React.Fragment key={`${keyPrefix}-${characterIndex}`}>
          {character}
        </React.Fragment>
      ) : (
        <span
          key={`${keyPrefix}-${characterIndex}`}
          className="writing-section__reveal-char"
          aria-hidden="true"
        >
          {character}
        </span>
      )
    );
  }

  return (text.match(/\S+|\s+/g) ?? []).map((token, tokenIndex) => {
    if (/^\s+$/.test(token)) {
      return <React.Fragment key={`${keyPrefix}-${tokenIndex}`}>{token}</React.Fragment>;
    }

    return (
      <span
        key={`${keyPrefix}-${tokenIndex}`}
        className="writing-section__reveal-word"
        aria-hidden="true"
      >
        {Array.from(token).map((character, characterIndex) => (
          <span
            key={`${keyPrefix}-${tokenIndex}-${characterIndex}`}
            className="writing-section__reveal-char"
          >
            {character}
          </span>
        ))}
      </span>
    );
  });
};

export const WritingSection: React.FC<WritingSectionProps> = ({ language = 'en' }) => {
  const copy = WRITING_COPY[language];
  const headerRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const header = headerRef.current;

    if (!header) {
      return undefined;
    }

    const chars = Array.from(
      header.querySelectorAll<HTMLElement>('.writing-section__reveal-char')
    );

    if (chars.length === 0) {
      return undefined;
    }

    let visualLines: HTMLElement[][] = [];
    let latestProgress = 0;
    let resizeFrame = 0;

    const measureVisualLines = () => {
      const nextLines: HTMLElement[][] = [];
      let currentTop: number | null = null;

      chars.forEach((char) => {
        const charTop = char.getBoundingClientRect().top;

        if (currentTop === null || Math.abs(charTop - currentTop) > 2) {
          nextLines.push([]);
          currentTop = charTop;
        }

        nextLines[nextLines.length - 1].push(char);
      });

      visualLines = nextLines;
    };

    const updateReveal = (progress: number) => {
      latestProgress = Math.min(1, Math.max(0, progress));

      visualLines.forEach((line, lineIndex) => {
        const lineProgress = resolveLineRevealProgress(
          latestProgress,
          lineIndex,
          visualLines.length
        ).toFixed(4);

        line.forEach((char) => {
          char.style.setProperty('--writing-line-progress', lineProgress);
        });
      });
    };

    const measureAndUpdate = () => {
      measureVisualLines();
      updateReveal(latestProgress);
    };

    measureAndUpdate();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      updateReveal(1);
      return undefined;
    }

    const trigger = ScrollTrigger.create({
      trigger: header,
      start: 'top 82%',
      end: 'bottom 22%',
      scrub: 0.9,
      invalidateOnRefresh: true,
      onUpdate: (instance) => updateReveal(instance.progress),
      onRefresh: (instance) => {
        measureVisualLines();
        updateReveal(instance.progress);
      },
    });

    updateReveal(trigger.progress ?? 0);

    const handleResize = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(measureAndUpdate);
    };
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(header);

    return () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeObserver.disconnect();
      trigger.kill();
    };
  }, [language]);

  useLayoutEffect(() => {
    const list = listRef.current;

    if (!list) {
      return undefined;
    }

    const items = Array.from(
      list.querySelectorAll<HTMLElement>('.writing-section__item')
    );

    if (items.length === 0) {
      return undefined;
    }

    const setItemProgress = (item: HTMLElement, progress: number) => {
      const clampedProgress = Math.min(1, Math.max(0, progress));
      item.style.setProperty('--writing-item-progress', clampedProgress.toFixed(4));
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => setItemProgress(item, 1));
      return undefined;
    }

    const triggers = items.map((item) => {
      setItemProgress(item, 0);

      const trigger = ScrollTrigger.create({
        trigger: item,
        start: 'top 88%',
        end: 'top 48%',
        scrub: 1.15,
        invalidateOnRefresh: true,
        onUpdate: (instance) => setItemProgress(item, instance.progress),
        onRefresh: (instance) => setItemProgress(item, instance.progress),
      });

      setItemProgress(item, trigger.progress ?? 0);
      return trigger;
    });

    return () => {
      triggers.forEach((trigger) => trigger.kill());
    };
  }, [language]);

  return (
    <section
      id="writing"
      className="writing-section"
      aria-labelledby="writing-section-title"
    >
      <div className="writing-section__inner">
        <header ref={headerRef} className="writing-section__header">
          <h2
            id="writing-section-title"
            className="writing-section__title"
            aria-label={copy.title}
          >
            {renderRevealText(copy.title, `${language}-title`)}
          </h2>
          <p className="writing-section__intro" aria-label={copy.intro}>
            {renderRevealText(copy.intro, `${language}-intro`)}
          </p>
        </header>

        <div ref={listRef} className="writing-section__list">
          {copy.articles.map((article) => (
            <article key={article.index} className="writing-section__item">
              <div className="writing-section__article-copy">
                <span className="writing-section__index">{article.index}</span>
                <div className="writing-section__article-text">
                  <h3 className="writing-section__article-title">
                    {article.href ? (
                      <Link
                        className="writing-section__article-link"
                        to={`${article.href}?lang=${language}`}
                      >
                        {article.title}
                      </Link>
                    ) : (
                      article.title
                    )}
                  </h3>
                  <p className="writing-section__summary">{article.summary}</p>
                </div>
              </div>

              <div className="writing-section__media">
                <img
                  src={article.image}
                  alt={article.imageAlt}
                  className="writing-section__image"
                  loading="lazy"
                />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
